"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { MarketOverlay } from "@/components/market/MarketOverlay";
import { Status } from "@/components/ui/Status";
import { products } from "@/content/facts";
import { productosPage } from "@/content/productos";
import { worldBus } from "@/lib/world-bus";
import { clamp, smoothstep } from "@/world/core/math";
import styles from "./ProductStage.module.css";

type Pick = "yvyra" | "tajy";

// El súper de Productos (guía 7.4). La escena es el mundo 3D de fondo: el hero lo
// muestra desde la vereda de enfrente y, cuando esta sección llena la pantalla, la
// cámara entra al local hasta lo que usa el producto elegido. Yvyra: el celular de la
// caja contestando una consulta. Tajy: la vista de la cámara del techo.
const LAYERS: Record<Pick, { chat: number; stock: number; cam: number; dev: number }> = {
  yvyra: { chat: 1, stock: 1, cam: 0, dev: 0 },
  tajy: { chat: 1, stock: 1, cam: 1, dev: 1 },
};

export function ProductStage() {
  const [pick, setPick] = useState<Pick>("yvyra");
  const ref = useRef<HTMLElement>(null);
  const pickRef = useRef<Pick>(pick);
  const l = LAYERS[pick];
  const t = productosPage.selector;

  /** Cuánto de la pantalla ocupa esta sección (1: la llena). */
  const fill = useCallback(() => {
    const el = ref.current;
    if (!el) return 0;
    const r = el.getBoundingClientRect();
    return 1 - clamp(Math.abs(r.top) / (window.innerHeight * 0.45));
  }, []);
  const visibility = useCallback(() => smoothstep(0.55, 0.85, fill()), [fill]);

  useEffect(() => {
    pickRef.current = pick;
  }, [pick]);

  // La cámara entra al súper cuando la sección llena la pantalla y vuelve a la vereda
  // cuando se va.
  useEffect(() => {
    let raf = 0;
    const tick = () => {
      raf = requestAnimationFrame(tick);
      const inside = fill() > 0.5;
      const view = inside ? pickRef.current : "front";
      if (worldBus.market.view !== view) {
        worldBus.market.view = view;
        if (inside) worldBus.market.replay++;
      }
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      worldBus.market.view = "front";
    };
  }, [fill]);

  return (
    <section ref={ref} className={styles.stage} aria-label={t.label}>
      <MarketOverlay visibility={visibility} />
      <div className={styles.panel}>
        <div className={styles.switch} role="group" aria-label={t.label}>
          {(["yvyra", "tajy"] as const).map((p) => (
            <button key={p} type="button" className={styles.option} aria-pressed={pick === p} onClick={() => setPick(p)}>
              <span className={styles.optionName}>{products[p].name}</span>
              <Status kind={products[p].status.kind}>{products[p].status.label}</Status>
            </button>
          ))}
        </div>
        <ul className={styles.layers} aria-live="polite">
          {(Object.keys(t.layers) as (keyof typeof t.layers)[]).map((k) => (
            <li key={k} data-on={l[k] ? "true" : undefined}>
              {t.layers[k]}
            </li>
          ))}
        </ul>
        <button type="button" className={styles.replay} onClick={() => worldBus.market.replay++}>
          {t.replay}
        </button>
      </div>
    </section>
  );
}
